import client from '@lib/api/client'
import { useQuery, queryOptions } from '@tanstack/react-query'
import type { GetProfileQueryResponse, GetProfile400, GetProfile401 } from '../../models/usersModels/GetProfile'
import type { QueryObserverOptions, UseQueryResult, QueryKey } from '@tanstack/react-query'

type GetProfileClient = typeof client<GetProfileQueryResponse, GetProfile400 | GetProfile401, never>
type GetProfile = {
  data: GetProfileQueryResponse
  error: GetProfile400 | GetProfile401
  request: never
  pathParams: never
  queryParams: never
  headerParams: never
  response: GetProfileQueryResponse
  client: {
    parameters: Partial<Parameters<GetProfileClient>[0]>
    return: Awaited<ReturnType<GetProfileClient>>
  }
}
export const getProfileQueryKey = () => [{ url: '/api/Users/profile' }] as const
export type GetProfileQueryKey = ReturnType<typeof getProfileQueryKey>
export function getProfileQueryOptions(options: GetProfile['client']['parameters'] = {}) {
  const queryKey = getProfileQueryKey()
  return queryOptions({
    queryKey,
    queryFn: async () => {
      const res = await client<GetProfile['data'], GetProfile['error']>({
        method: 'get',
        url: `/api/Users/profile`,
        ...options
      })
      return res.data
    }
  })
}
/**
 * @summary Get the profile information for the current user
 * @link /api/Users/profile
 */
export function useGetProfile<
  TData = GetProfile['response'],
  TQueryData = GetProfile['response'],
  TQueryKey extends QueryKey = GetProfileQueryKey
>(
  options: {
    query?: Partial<QueryObserverOptions<GetProfile['response'], GetProfile['error'], TData, TQueryData, TQueryKey>>
    client?: GetProfile['client']['parameters']
  } = {}
): UseQueryResult<TData, GetProfile['error']> & {
  queryKey: TQueryKey
} {
  const { query: queryOptions, client: clientOptions = {} } = options ?? {}
  const queryKey = queryOptions?.queryKey ?? getProfileQueryKey()
  const query = useQuery({
    ...(getProfileQueryOptions(clientOptions) as unknown as QueryObserverOptions),
    queryKey,
    ...(queryOptions as unknown as Omit<QueryObserverOptions, 'queryKey'>)
  }) as UseQueryResult<TData, GetProfile['error']> & {
    queryKey: TQueryKey
  }
  query.queryKey = queryKey as TQueryKey
  return query
}
